import { Booking } from './Booking'
import { BookingAuditEntry } from './BookingAuditEntry'
import { BookingStatus, BookingStatusType, CancellationReason } from './BookingStatus'
import { CurrencyUnitPipe } from '@app/features/shared/pipes/currency-unit.pipe'
import { Helper } from '@app/helpers/utilities/helper'
import { Table } from './Table'
import { TableNamePipe } from '../features/shared/pipes/table-name.pipe'
import { Time } from '@app/domain/Time'
import { Venue } from './Venue'
import { paymentDeclineExplanation } from './PaymentDeclineMessage'

type AuditEntryChangeLabel = {
    title: string,
    subtitle?: string,
    performedBySystem?: boolean,
    date?: Date,
    groupKey?: string,
}

export type BookingHistoryEntry = {
    title: string,
    subtitle?: string,
    badges?: { label: string, value: string }[],
    date: Date,
    performedBy?: 'customer' | 'merchant',
    userName?: string | null,
    userEmailAddress?: string | null,
    groupKey?: string,
}

type AuditEntryPerformer = {
    performedBy?: 'customer' | 'merchant',
    userName?: string | null,
    userEmailAddress?: string | null,
}

const STATUS_EVENT_DEDUPE_WINDOW_MS = 1000

export class BookingHistoryFactory {

    constructor(
        private booking: Booking,
        private auditLog: BookingAuditEntry[],
        private currencyUnitPipe: CurrencyUnitPipe,
        private tableNamePipe: TableNamePipe,
        private venue: Venue
    ) { }

    make(): BookingHistoryEntry[] {
        return [
            ...this.adaptStatusHistoryToLabels(),
            ...this.adaptReminderToLabels(),
            ...this.adaptCancellationChargeToLabels(),
            ...this.adaptAuditLogToLabels(),
            ...this.adaptStatusServiceHistoryToLabels(),
            ...this.adaptAuditLogInitialValuesForBlockLevelAttributes(),
        ]
            .sort((a, b) => {
                return b.date.getTime() - a.date.getTime()
            })
    }

    private adaptStatusHistoryToLabels() {
        const statusEventIndexesWithMatchingAuditEntries = this.statusEventIndexesWithMatchingAuditEntries()
        return this.booking.statusEvents
            .filter((_, index) => !statusEventIndexesWithMatchingAuditEntries.has(index))
            .map((statusEvent) => {
                return {
                    ...this.adaptBookingStatusToLabels(statusEvent),
                    date: statusEvent.dateTime,
                }
            })
    }

    private statusEventIndexesWithMatchingAuditEntries(): Set<number> {
        const matchedStatusEventIndexes = new Set<number>()
        this.auditLog.forEach((auditEntry) => {
            const newStatus = auditEntry.changes['newStatus'] as string | undefined
            if (!newStatus || this.adaptNewStatusAuditEntryToLabel(newStatus) === null) {
                return
            }
            const closestStatusEventIndex = this.closestUnmatchedStatusEventIndex(
                newStatus,
                auditEntry.dateTime,
                matchedStatusEventIndexes
            )
            if (closestStatusEventIndex !== null) {
                matchedStatusEventIndexes.add(closestStatusEventIndex)
            }
        })
        return matchedStatusEventIndexes
    }

    private closestUnmatchedStatusEventIndex(
        statusName: string,
        dateTime: Date,
        matchedStatusEventIndexes: Set<number>
    ): number | null {
        let closestIndex: number | null = null
        let closestDistance: number | null = null
        this.booking.statusEvents.forEach((statusEvent, index) => {
            if (matchedStatusEventIndexes.has(index)) {
                return
            }
            if (BookingStatusType[statusEvent.type] !== statusName) {
                return
            }
            const distance = Math.abs(statusEvent.dateTime.getTime() - dateTime.getTime())
            if (distance > STATUS_EVENT_DEDUPE_WINDOW_MS) {
                return
            }
            if (closestDistance === null || distance < closestDistance) {
                closestIndex = index
                closestDistance = distance
            }
        })
        return closestIndex
    }

    private adaptReminderToLabels() {
        const reminderSentHistory = this.booking.reminders
            .map((reminder) => ({
                title: 'Booking Reminder Email Sent',
                date: reminder.sentDateTime,
            }))
        const reminderConfirmationHistory = this.booking.reminders
            .filter((reminder) => {
                return reminder.confirmedDateTime !== null
            })
            .map((reminder) => ({
                title: 'Customer Confirmed Booking',
                date: reminder.confirmedDateTime!,
            }))
        return [
            ...reminderSentHistory,
            ...reminderConfirmationHistory,
        ]
    }

    private adaptCancellationChargeToLabels() {
        if (!this.booking.cancellation) {
            return []
        }
        return this.booking.cancellation.charges
            .map((charge) => {
                const amount = this.currencyUnitPipe.transform(
                    null,
                    charge.amount,
                    charge.currencyCode
                )
                const subtitle = `Card charged ${amount}`
                return {
                    title: 'Cancellation Charge',
                    subtitle: subtitle,
                    date: charge.dateCreated,
                }
            })
    }

    private adaptAuditLogToLabels() {
        return this.auditLog
            .flatMap((auditEntry, index) => {
                const changeLabels = this.adaptAuditEntryToLabels(auditEntry, index)
                return changeLabels.map((changeLabel) => {
                    const performer = this.getAuditEntryPerformer(auditEntry)
                    let performedBy = performer.performedBy
                    const labelPerformedBySystem = changeLabel.performedBySystem
                    if (labelPerformedBySystem) {
                        performedBy = undefined
                    }
                    return {
                        title: changeLabel.title,
                        subtitle: changeLabel.subtitle,
                        date: changeLabel.date ?? auditEntry.dateTime,
                        performedBy: performedBy,
                        userName: performer.userName,
                        userEmailAddress: performer.userEmailAddress,
                        groupKey: changeLabel.groupKey,
                    }
                })
            })
    }

    private adaptAuditLogInitialValuesForBlockLevelAttributes() {
        let labels: BookingHistoryEntry[] = []
        const auditEntry = this.auditLog[0]
        if (!auditEntry) {
            return []
        }
        const performer = this.getAuditEntryPerformer(auditEntry)
        const merchantNotes = auditEntry.changes['merchantNotes']
        if (merchantNotes) {
            labels.push({
                title: 'Merchant Notes',
                subtitle: merchantNotes,
                date: auditEntry.dateTime,
                performedBy: performer.performedBy,
                userName: performer.userName,
                userEmailAddress: performer.userEmailAddress,
            })
        }
        const customerNotes = auditEntry.changes['notes']
        if (customerNotes) {
            labels.push({
                title: 'Customer Message',
                subtitle: customerNotes,
                date: auditEntry.dateTime,
                performedBy: performer.performedBy,
                userName: performer.userName,
                userEmailAddress: performer.userEmailAddress,
            })
        }
        return labels
    }

    private getAuditEntryPerformer(auditEntry: BookingAuditEntry): AuditEntryPerformer {
        const apiKeyName = auditEntry.changes['api_key_name']
        if (apiKeyName) {
            return {
                performedBy: 'merchant',
                userName: apiKeyName,
                userEmailAddress: null,
            }
        }
        return {
            performedBy: auditEntry.userId !== null ? 'merchant' : 'customer',
            userName: auditEntry.userName,
            userEmailAddress: auditEntry.userEmailAddress,
        }
    }

    private adaptBookingStatusToLabels(status: BookingStatus) {
        switch (status.type) {
        case BookingStatusType.Requested:
            { 
                const requestedTitle = 'Customer Requested Booking'
                return {
                    title: requestedTitle,
                    badges: this.getUtmBadges(status) || undefined,
                } 
            }
        case BookingStatusType.PendingPayment:
            return {
                ...this.pendingPaymentLabel(status),
                badges: this.getUtmBadges(status) || undefined,
            }
        case BookingStatusType.Booked:
            { 
                const bookedTitle = 'Booking Confirmed'
                return {
                    title: bookedTitle,
                    badges: this.getUtmBadges(status) || undefined,
                }
            }
        case BookingStatusType.Cancelled:
            return this.cancelledLabel(status)
        case BookingStatusType.NoShow:
            return {
                title: 'Customer No Show',
            }
        case BookingStatusType.Waiting:
            return {
                title: 'Waiting at Bar',
            }
        case BookingStatusType.PartiallySeated:
            return {
                title: 'Partially Seated',
            }
        case BookingStatusType.Seated:
            return {
                title: 'Customer Seated',
            }
        case BookingStatusType.Finished:
            return {
                title: 'Booking Finished',
            }
        case BookingStatusType.Rejected:
            return {
                title: 'Booking Rejected',
            }
        }
    }

    private adaptNewStatusAuditEntryToLabel(newStatus: string): { title: string, subtitle?: string } | null {
        switch (newStatus) {
        case 'Booked':
            return { title: 'Booking Confirmed' }
        case 'PendingPayment':
            return { title: 'Pending Payment' }
        case 'NoShow':
            return { title: 'Customer No Show' }
        case 'Waiting':
            return { title: 'Waiting at Bar' }
        case 'PartiallySeated':
            return { title: 'Partially Seated' }
        case 'Seated':
            return { title: 'Customer Seated' }
        case 'Finished':
            return { title: 'Booking Finished' }
        default:
            return null
        }
    }

    private getUtmBadges(status: BookingStatus): {label: string, value: string}[] {
        const badges: {label: string, value: string}[] = []
        
        const source = status.getUtmSource()
        if (source) {
            badges.push({label: 'source', value: source.toLocaleLowerCase() !== 'rwg' ? source : 'Google'})
        }
        
        const medium = status.getUtmMedium()
        if (source && source.toLocaleLowerCase() !== 'rwg' && medium) { 
            badges.push({label: 'medium', value: medium})
        }
        
        const campaign = status.getUtmCampaign()
        if (source && source.toLocaleLowerCase() !== 'rwg' && campaign) {
            badges.push({label: 'campaign', value: campaign})
        }
        
        return badges
    }

    private pendingPaymentLabel(status: BookingStatus) {
        if (status.cancellationCharge()) {
            return {
                title: 'Pending Card Details',
            }
        }
        if (status.depositCharge()) {
            return {
                title: 'Pending Deposit Payment',
            }
        }
        return {
            title: 'Pending Payment',
        }
    }

    private cancelledLabel(status: BookingStatus) {
        const information = status.cancellationInformation()
        if (!information) {
            return {
                title: 'Booking Cancelled',
            }
        }
        if (information.reason === CancellationReason.ChargeExpired) {
            return {
                title: 'Booking Cancelled',
                subtitle: 'Pending Charge Expired',
            }
        }
        if (information.cancelledByCustomer) {
            const subtitle = information.cancellationReasonDisplayName
                ? `Cancelled by Customer: ${information.cancellationReasonDisplayName}`
                : 'Cancelled by Customer'
            return {
                title: 'Booking Cancelled',
                subtitle: subtitle,
            }
        }
        const emailAddress = information.cancelledByUserEmailAddress
        if (emailAddress) {
            const subtitle = information.cancellationReasonDisplayName
                ? `Cancelled by ${emailAddress}: ${information.cancellationReasonDisplayName}`
                : `Cancelled by ${emailAddress}`
            return {
                title: 'Booking Cancelled by Merchant',
                subtitle: subtitle,
            }
        }
        if (information.cancellationReasonDisplayName) {
            return {
                title: 'Booking Cancelled by Merchant',
                subtitle: `Cancelled by Merchant: ${information.cancellationReasonDisplayName}`,
            }
        }
        return {
            title: 'Booking Cancelled',
        }
    }

    private adaptAuditEntryToLabels(auditEntry: BookingAuditEntry, index: number) {
        const changes: AuditEntryChangeLabel[] = []
        this.makeAuditEntryChangeLabels(auditEntry, index)
            .forEach((change) => changes.push(change))
        return changes
    }

    private makeAuditEntryChangeLabels(auditEntry: BookingAuditEntry, index: number) {
        const changes: AuditEntryChangeLabel[] = []
        if (auditEntry.changes['attributedToUserName']) {
            const attributedToUserName = auditEntry.changes['attributedToUserName']
            changes.push({
                title: 'Booking Created',
                subtitle: `Created by ${attributedToUserName}`,
            })
        }
        if (auditEntry.changes['newStatus']) {
            const statusLabel = this.adaptNewStatusAuditEntryToLabel(auditEntry.changes['newStatus'])
            if (statusLabel) {
                changes.push(statusLabel)
            }
        }
        if (auditEntry.changes['removedCancellationId']) {
            changes.push({
                title: 'Cancellation Removed',
                subtitle: auditEntry.changes['cancellationCardDeletionConfirmed'] === false
                    ? 'Provider card deletion was not confirmed'
                    : undefined,
            })
        }
        const refundedAmount = auditEntry.changes['refundedAmount'] as number | undefined
        const currencyCode = auditEntry.changes['currencyCode'] as string | undefined
        if (refundedAmount !== undefined) {
            const amount = this.currencyUnitPipe.transform(
                null,
                refundedAmount,
                currencyCode
            )
            changes.push({
                title: 'Payment Refunded',
                subtitle: amount ? `Amount: ${amount}` : undefined,
            })
        }
        if (auditEntry.changes['cancellationChargeFailureCode'] !== undefined) {
            const attemptedStatus = auditEntry.changes['cancellationChargeFailureStatus'] as string | null
            const title = attemptedStatus === 'NoShow'
                ? 'No Show Charge Failed'
                : 'Cancellation Charge Failed'
            changes.push({
                title,
                subtitle: paymentDeclineExplanation(
                    auditEntry.changes['cancellationChargeFailureCode']
                ),
            })
        }
        const changesAreInitialValues = index === 0
        if (changesAreInitialValues) {
            return changes
        }
        if (auditEntry.changes['start']) {
            const date = new Date(auditEntry.changes['start'])
            const previousDate = this.getBookingStartBeforeAuditEntryAtIndex(index)
            const dateHasChanged = previousDate
                && date.toDateString() !== previousDate.toDateString()
            if (dateHasChanged) {
                const subtitle = `From ${Helper.makeLocalISOFormattedDateString(previousDate)} `
                + `to ${Helper.makeLocalISOFormattedDateString(date)}`
                changes.push({
                    title: 'Date Changed',
                    subtitle: subtitle,
                })
            }
            const time = new Time(null, date.getHours(), date.getMinutes())
            const previousTime = previousDate ? new Time(null, previousDate.getHours(), previousDate.getMinutes()) : null
            const timeHasChanged = previousTime && !time.equals(previousTime)
            if (timeHasChanged) {
                const subtitle = `From ${previousTime.toString()} to ${time.toString()}`
                changes.push({
                    title: 'Time Changed',
                    subtitle: subtitle,
                })
            }
        }
        if (auditEntry.changes['size']) {
            const size = auditEntry.changes['size']
            let subtitle = `${size} covers`
            const previousSize = this.getSizeBeforeAuditEntryAtIndex(index)
            const sizeHasChanged = previousSize && size !== previousSize
            if (sizeHasChanged) {
                subtitle = `From ${previousSize} to ${size} covers`
            }
            changes.push({
                title: 'Party Size Changed',
                subtitle: subtitle,
            })
        }
        if (auditEntry.changes['duration']) {
            const duration = auditEntry.changes['duration']
            let subtitle = `${duration} minutes`
            const previousDuration = this.getDurationBeforeAuditEntryAtIndex(index)
            const durationHasChanged = previousDuration && duration !== previousDuration
            if (durationHasChanged) {
                subtitle = `From ${previousDuration} to ${duration} minutes`
            }
            changes.push({
                title: 'Duration Changed',
                subtitle: subtitle,
            })
        }
        if (auditEntry.changes['tableIds']) {
            const tableIds = auditEntry.changes['tableIds'] as string[]
            const tables = tableIds
                .map((tableId) => this.venue.areaWithTableId(tableId)?.getTableWithId(tableId))
                .filter((table) => table !== undefined)
                .filter((table) => table !== null) as Table[]
            let subtitle = this.tableNamePipe.transform(undefined, undefined, 'short', tables)
            const previousTableIds = this.getTableIdsBeforeAuditEntryAtIndex(index)
            const tableHasChanged = previousTableIds
                && tableIds.join(',') !== previousTableIds.join(',')
            if (tableHasChanged) {
                const previousTables = previousTableIds
                    .map((tableId) => this.venue.areaWithTableId(tableId)?.getTableWithId(tableId))
                    .filter((table) => table !== undefined)
                    .filter((table) => table !== null) as Table[]
                const previousSubtitle = this.tableNamePipe.transform(
                    undefined,
                    undefined,
                    'short',
                    previousTables
                )
                subtitle = `From ${previousSubtitle} to ${subtitle}`
            }
            changes.push({
                title: 'Table Changed',
                subtitle: subtitle,
            })
        }
        if (auditEntry.changes['reasonId']) {
            const reasonId = auditEntry.changes['reasonId'] as string
            const reason = this.venue.reasonWithId(reasonId)
            let subtitle = reason?.displayName
            const previousReasonId = this.getReasonIdBeforeAuditEntryAtIndex(index)
            const reasonHasChanged = previousReasonId && reasonId !== previousReasonId
            if (reasonHasChanged && previousReasonId) {
                const previousReason = this.venue.reasonWithId(previousReasonId)
                const previousSubtitle = previousReason?.displayName
                subtitle = `From ${previousSubtitle} to ${subtitle}`
            }
            changes.push({
                title: 'Reason Changed',
                subtitle: subtitle,
            })
        }
        if (auditEntry.changes['notes']) {
            const notes = auditEntry.changes['notes'] as string
            changes.push({
                title: 'Customer Message',
                subtitle: notes,
            })
        }
        if (auditEntry.changes['notes'] === null) {
            changes.push({
                title: 'Customer Message Deleted',
            })
        }
        if (auditEntry.changes['merchantNotes']) {
            const notes = auditEntry.changes['merchantNotes'] as string
            changes.push({
                title: 'Merchant Notes',
                subtitle: notes,
            })
        }
        if (auditEntry.changes['merchantNotes'] === null) {
            changes.push({
                title: 'Merchant Notes Deleted',
            })
        }
        if (auditEntry.changes['customerId']) {
            const customerId = auditEntry.changes['customerId'] as string
            const detached = customerId === null
            const attached = customerId !== null
            if (detached) {
                changes.push({
                    title: 'Detached from Customer Profile',
                })
            }
            if (attached) {
                changes.push({
                    title: 'Attached to Customer Profile',
                })
            }
        }
        if (auditEntry.changes['serviceStatus'] !== undefined) {
            const serviceStatus = auditEntry.changes['serviceStatus'] as string | null
            if (serviceStatus) {
                const formatted = serviceStatus.charAt(0).toUpperCase() + serviceStatus.slice(1).toLowerCase()
                changes.push({
                    title: 'Service Status Changed',
                    subtitle: formatted,
                })
            } else {
                changes.push({
                    title: 'Service Status Cleared',
                })
            }
        }
        if (auditEntry.changes['pendingPayment']) {
            const cancellationAmount = auditEntry.changes['cancellationChargeAmount'] as number
            if (cancellationAmount !== undefined) {
                const amount = this.currencyUnitPipe.transform(
                    null,
                    cancellationAmount,
                    'GBP'
                )
                changes.push({
                    title: 'Cancellation Charge Added',
                    subtitle: `Amount: ${amount}`,
                })
            }
            const depositAmount = auditEntry.changes['depositChargeAmount'] as number
            if (depositAmount !== undefined) {
                const amount = this.currencyUnitPipe.transform(
                    null,
                    depositAmount,
                    'GBP'
                )
                changes.push({
                    title: 'Deposit Charge Added',
                    subtitle: `Amount: ${amount}`,
                })
            }
        }
        if (
            auditEntry.changes['paymentMethodSetupErrorCode'] !== undefined
            || auditEntry.changes['paymentMethodSetupErrorMessage'] !== undefined
        ) {
            const errorCode = auditEntry.changes['paymentMethodSetupErrorCode'] as string | null
            const errorMessage = auditEntry.changes['paymentMethodSetupErrorMessage'] as string | null
            const subtitleParts: string[] = []
            if (errorCode) {
                subtitleParts.push(`Code: ${errorCode}`)
            }
            if (errorMessage) {
                subtitleParts.push(errorMessage)
            }
            changes.push({
                title: 'Payment Method Setup Failed',
                subtitle: subtitleParts.length > 0 ? subtitleParts.join(' - ') : undefined,  
                performedBySystem: true,
            })
        }
        const updatedChargeAmount = auditEntry.changes['updatedChargeAmount'] as number
        if (updatedChargeAmount !== undefined) {
            const amount = this.currencyUnitPipe.transform(
                null,
                updatedChargeAmount,
                'GBP'
            )
            changes.push({
                title: 'Charge Amount Updated',
                subtitle: `Amount: ${amount}`,
            })
        }
        const bookingMessageEntry = this.makeBookingMessageEntry(auditEntry)
        if (bookingMessageEntry) {
            changes.push(bookingMessageEntry)
        }
        const emailEventEntry = this.makeEmailEventEntry(auditEntry)
        if (emailEventEntry) {
            changes.push(emailEventEntry)
        }
        const posSyncEntry = this.makePOSSyncEntry(auditEntry)
        if (posSyncEntry) {
            changes.push(posSyncEntry)
        }
        const answers = auditEntry.changes['answers'] as { question: { id: string, text: string }, oldAnswer: string | null, newAnswer: string }[] | undefined
        if (answers && answers.length > 0) {
            answers.forEach((changedAnswer) => {
                changes.push({
                    title: 'Answer Changed',
                    subtitle: `${changedAnswer.question.text}: From ${changedAnswer.oldAnswer} To ${changedAnswer.newAnswer}`,
                })
            })
        }
        return changes
    }

    private makeBookingMessageEntry(auditEntry: BookingAuditEntry) {
        if (!auditEntry.changes['messageType']) {
            return
        }
        const messageType = auditEntry.changes['messageType'] as string
        const isEmail = auditEntry.changes['isEmail'] as boolean
        const isSMS = auditEntry.changes['isSMS'] as boolean
        const subtitle = 'Sent via ' + (isEmail ? 'Email' : '') + (isSMS ? 'SMS' : '')
        let title = ''
        switch (messageType) {
        case 'CONFIRMATION':
            title = 'Booking Confirmation Sent'
            break
        case 'MODIFICATION':
            title = 'Booking Modification Sent'
            break
        case 'CANCELLATION':
            title = 'Booking Cancellation Sent'
            break
        case 'REJECTION':
            title = 'Booking Rejection Sent'
            break
        case 'NO_SHOW':
            title = 'No Show Sent'
            break
        case 'PENDING_PAYMENT':
            title = 'Pending Payment Sent'
            break
        case 'PENDING_PAYMENT_REMINDER':
            title = 'Pending Payment Reminder Sent'
            break
        case 'REMINDER':
            title = 'Booking Reminder Sent'
            break
        case 'FEEDBACK':
            title = 'Booking Feedback Request Sent'
            break
        case 'REQUEST':
            title = 'Booking Request Acknowledgement Sent'
            break
        case 'PRE_ORDER':
            title = 'Pre-Order Menu Link Sent'
            break
        }
        return {
            title: title,
            subtitle: subtitle,
            performedBySystem: true,
            groupKey: this.messageGroupKey(auditEntry),
        }
    }

    private makeEmailEventEntry(auditEntry: BookingAuditEntry): AuditEntryChangeLabel | undefined {
        const emailEventType = auditEntry.changes['emailEventType'] as string | undefined
        if (!emailEventType) {
            return
        }
        const eventAction = this.emailEventAction(emailEventType)
        if (!eventAction) {
            return
        }
        const messageId = auditEntry.changes['messageId'] as string | undefined
        const matchingMessageAuditEntry = messageId
            ? this.findEmailMessageAuditEntry(messageId)
            : undefined
        const messageDisplayName = matchingMessageAuditEntry
            ? this.bookingMessageDisplayName(matchingMessageAuditEntry.changes['messageType'] as string)
            : null
        const emailTitlePrefix = messageDisplayName ? `${messageDisplayName} Email` : 'Email'
        return {
            title: `${emailTitlePrefix} ${eventAction}`,
            subtitle: this.emailEventSubtitle(auditEntry, emailEventType),
            date: this.emailEventDate(auditEntry),
            performedBySystem: true,
            groupKey: messageId,
        }
    }

    private messageGroupKey(auditEntry: BookingAuditEntry): string | undefined {
        const messageId = auditEntry.changes['messageId']
        if (typeof messageId !== 'string') {
            return undefined
        }
        const trimmedMessageId = messageId.trim()
        return trimmedMessageId.length > 0 ? trimmedMessageId : undefined
    }

    private findEmailMessageAuditEntry(messageId: string) {
        return this.auditLog.find((entry) => {
            return entry.changes['messageId'] === messageId
                && entry.changes['messageType']
                && entry.changes['isEmail'] !== false
        })
    }

    private emailEventAction(emailEventType: string): string | null {
        switch (emailEventType) {
        case 'DELIVERY':
            return 'Delivered'
        case 'COMPLAINT':
            return 'Complaint Received'
        case 'HARD_BOUNCE':
            return 'Hard Bounced'
        default:
            return null
        }
    }

    private emailEventSubtitle(auditEntry: BookingAuditEntry, emailEventType: string): string | undefined {
        if (emailEventType === 'DELIVERY') {
            return this.deliveryEventSubtitle(auditEntry)
        }
        const reason = this.emailEventReason(auditEntry)
        const label = this.emailEventReasonLabel(emailEventType, reason)
        return label.length > 0 ? label : undefined
    }

    private deliveryEventSubtitle(auditEntry: BookingAuditEntry): string | undefined {
        const recipientEmailAddresses = this.emailEventRecipientEmailAddresses(auditEntry)
        if (recipientEmailAddresses.length === 0) {
            return undefined
        }
        if (recipientEmailAddresses.length === 1) {
            return `Delivered to ${recipientEmailAddresses[0]}`
        }
        return `Delivered to ${recipientEmailAddresses.join(', ')}`
    }

    private emailEventRecipientEmailAddresses(auditEntry: BookingAuditEntry): string[] {
        const recipientEmailAddresses = auditEntry.changes['emailEventRecipientEmailAddresses']
        if (!Array.isArray(recipientEmailAddresses)) {
            return []
        }
        return recipientEmailAddresses
            .map((recipientEmailAddress) => `${recipientEmailAddress}`.trim())
            .filter((recipientEmailAddress) => recipientEmailAddress.length > 0)
            .filter((recipientEmailAddress, index, allRecipientEmailAddresses) => {
                return allRecipientEmailAddresses.indexOf(recipientEmailAddress) === index
            })
    }

    private emailEventReason(auditEntry: BookingAuditEntry): string | undefined {
        const reason = auditEntry.changes['emailEventReason']
        if (typeof reason !== 'string') {
            return undefined
        }
        const trimmedReason = reason.trim()
        return trimmedReason.length > 0 ? trimmedReason : undefined
    }

    private emailEventReasonLabel(emailEventType: string, reason?: string): string {
        switch (emailEventType) {
        case 'DELIVERY':
            return ''
        case 'COMPLAINT':
            return this.complaintReasonLabel(reason)
        case 'HARD_BOUNCE':
            return reason ? this.hardBounceReasonLabel(reason) : 'The email could not be delivered'
        default:
            return reason || ''
        }
    }

    private complaintReasonLabel(reason?: string): string {
        switch (reason?.toLowerCase()) {
        case 'abuse':
            return 'The recipient reported this email as spam or abuse'
        case 'auth-failure':
            return 'The recipient reported an email authentication issue'
        case 'fraud':
            return 'The recipient reported this email as fraud or phishing'
        case 'not-spam':
            return 'The recipient reported this email was incorrectly marked as spam'
        case 'other':
            return 'The recipient submitted a complaint about this email'
        case 'virus':
            return 'The recipient reported this email contained a virus'
        default:
            return 'The recipient submitted a complaint about this email'
        }
    }

    private hardBounceReasonLabel(reason: string): string {
        const normalizedReason = reason.toLowerCase()
        if (normalizedReason.startsWith('permanent: noemail')) {
            return 'The recipient email address could not be identified'
        }
        if (normalizedReason.startsWith('permanent: suppressed')) {
            return 'This email address is on Amazon SES\'s suppression list'
        }
        if (normalizedReason.startsWith('permanent: onaccountsuppressionlist')) {
            return 'This email address is on your account suppression list'
        }
        if (normalizedReason.startsWith('permanent: unsubscribedrecipient')) {
            return 'This recipient has unsubscribed from email'
        }
        if (this.matchesAnyReasonPattern(normalizedReason, [
            '5.1.1',
            'user unknown',
            'unknown user',
            'no such user',
            'recipient address rejected',
            'mailbox unavailable',
            'does not exist',
            'bad destination mailbox address',
            'invalid recipient',
        ])) {
            return 'The email address does not exist'
        }
        if (this.matchesAnyReasonPattern(normalizedReason, [
            'domain not found',
            'host not found',
            'unknown domain',
            'dns',
        ])) {
            return 'The recipient email domain could not be found'
        }
        if (this.matchesAnyReasonPattern(normalizedReason, [
            'mailbox full',
            'over quota',
            'quota exceeded',
        ])) {
            return 'The recipient mailbox is full'
        }
        if (this.matchesAnyReasonPattern(normalizedReason, [
            'access denied',
            'blocked',
            'rejected',
            'policy',
            'disabled',
            'not accepting mail',
        ])) {
            return 'The recipient email provider rejected the email'
        }
        if (normalizedReason.startsWith('permanent: general')) {
            return 'The recipient email provider rejected the email permanently'
        }
        return 'The email could not be delivered'
    }

    private matchesAnyReasonPattern(reason: string, patterns: string[]): boolean {
        return patterns.some((pattern) => reason.includes(pattern))
    }

    private emailEventDate(auditEntry: BookingAuditEntry): Date | undefined {
        const timestamp = auditEntry.changes['emailEventTimestamp']
        if (typeof timestamp !== 'string') {
            return undefined
        }
        const date = new Date(timestamp)
        return Number.isNaN(date.getTime()) ? undefined : date
    }

    private bookingMessageDisplayName(messageType: string): string | null {
        switch (messageType) {
        case 'CONFIRMATION':
            return 'Booking Confirmation'
        case 'MODIFICATION':
            return 'Booking Modification'
        case 'CANCELLATION':
            return 'Booking Cancellation'
        case 'REJECTION':
            return 'Booking Rejection'
        case 'NO_SHOW':
            return 'No Show'
        case 'PENDING_PAYMENT':
            return 'Pending Payment'
        case 'PENDING_PAYMENT_REMINDER':
            return 'Pending Payment Reminder'
        case 'REMINDER':
            return 'Booking Reminder'
        case 'FEEDBACK':
            return 'Booking Feedback Request'
        case 'REQUEST':
            return 'Booking Request Acknowledgement'
        case 'PRE_ORDER':
            return 'Pre-Order Menu Link'
        default:
            return null
        }
    }

    private makePOSSyncEntry(auditEntry: BookingAuditEntry) {
        if (!auditEntry.changes['posSyncEventType']) {
            return
        }
        const eventType = auditEntry.changes['posSyncEventType'] as string
        const posSource = auditEntry.changes['posSource'] as string
        const message = auditEntry.changes['posSyncMessage'] as string | undefined
        let title = ''
        switch (eventType) {
        case 'CREATED':
            title = `Synced to ${posSource}`
            break
        case 'UPDATED':
            title = `Updated on ${posSource}`
            break
        case 'CANCELLED':
            title = `Cancelled on ${posSource}`
            break
        case 'SKIPPED':
            title = `Sync to ${posSource} Skipped`
            break
        case 'FAILED':
            title = `Sync to ${posSource} Failed`
            break
        }
        return {
            title: title,
            subtitle: message,
            performedBySystem: true,
        }
    }

    private getBookingStartBeforeAuditEntryAtIndex(index: number) {
        if (index === 0) {
            return null
        }
        for (let i = index - 1; i >= 0; i--) {
            const entry = this.auditLog[i]
            if (entry.changes['start']) {
                return new Date(entry.changes['start'])
            }
        }
        return null
    }

    private getDurationBeforeAuditEntryAtIndex(index: number) {
        if (index === 0) {
            return null
        }
        for (let i = index - 1; i >= 0; i--) {
            const entry = this.auditLog[i]
            if (entry.changes['duration']) {
                return entry.changes['duration'] as number
            }
        }
        return null
    }

    private getTableIdsBeforeAuditEntryAtIndex(index: number) {
        if (index === 0) {
            return null
        }
        for (let i = index - 1; i >= 0; i--) {
            const entry = this.auditLog[i]
            if (entry.changes['tableIds']) {
                return entry.changes['tableIds'] as string[]
            }
        }
        return null
    }

    private getReasonIdBeforeAuditEntryAtIndex(index: number) {
        if (index === 0) {
            return null
        }
        for (let i = index - 1; i >= 0; i--) {
            const entry = this.auditLog[i]
            if (entry.changes['reasonId']) {
                return entry.changes['reasonId'] as string
            }
        }
        return null
    }

    private getSizeBeforeAuditEntryAtIndex(index: number) {
        if (index === 0) {
            return null
        }
        for (let i = index - 1; i >= 0; i--) {
            const entry = this.auditLog[i]
            if (entry.changes['size']) {
                return entry.changes['size'] as number
            }
        }
        return null
    }

    private adaptStatusServiceHistoryToLabels() {
        return this.booking.statusEvents
            .filter((statusEvent) => statusEvent.markedAsRunningLateDate() !== null)
            .map((statusEvent) => {
                return {
                    title: 'Customer is Running Late',
                    date: statusEvent.markedAsRunningLateDate()!,
                    performedBy: 'merchant',
                }
            })
    }
}
